import { inject } from "@angular/core";
import type { CanActivateFn, UrlTree } from "@angular/router";
import { WEB3_MERGE_PHASE, Web3Facade } from "@modules/web3/+state/web3.facade";
import { Observable, switchMap, of, map, combineLatest, catchError } from "rxjs";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { Web3NavigationService } from "@modules/web3/services/web3.navigation";
import { extractBasePath } from "@modules/trade/helpers/helper";
import { IWeb3MigrationStatusResponse } from "../interface/web3.remote.interface";

export const web3AutoMigrateGuardFn: CanActivateFn = (route, state): Observable<boolean | UrlTree> => {
	const facade = inject(Web3Facade);
	const navigation = inject(Web3NavigationService);
	const translationService = inject(TranslationService);
	const infoService = inject(InfoService);

	const basePath = extractBasePath(state.url);

	const isEstimationAmountEnoughWithFallBack$ = (phase: WEB3_MERGE_PHASE): Observable<boolean> => {
		if (phase === WEB3_MERGE_PHASE.PostMerge) {
			return facade.isSelectedBalanceEnough$();
		}
		//fallback to check selected balance
		return facade.getEstimation$().pipe(
			map(({ amount }) => +amount > 0),
			catchError(() => facade.isSelectedBalanceEnough$())
		);
	};

	const status$: Observable<IWeb3MigrationStatusResponse> = facade
		.getSignUpStatus$()
		.pipe(catchError(() => of({ signedUp: false })));

	return facade.getMergePhase().pipe(
		switchMap((result) => {
			let { phase } = result;

			//cut-off or merged or announced phase
			if (
				phase === WEB3_MERGE_PHASE.Cutoff ||
				phase === WEB3_MERGE_PHASE.Merge ||
				phase === WEB3_MERGE_PHASE.Announced
			) {
				infoService.setInfo(translationService.translate("web3_migration_warning_alert"));
				return of(navigation.createUrlTree(basePath));
			}

			//pre-merge phase
			if (phase === WEB3_MERGE_PHASE.PreMerge || phase === WEB3_MERGE_PHASE.PostMerge) {
				return combineLatest({
					status: status$,
					hasEnoughBalance: isEstimationAmountEnoughWithFallBack$(phase)
				}).pipe(
					map(({ status, hasEnoughBalance }) => {
						if (status?.signedUp && phase === WEB3_MERGE_PHASE.PreMerge) {
							infoService.setSuccess(translationService.translate("web3_migration_success_alert"));
							return navigation.createUrlTree(basePath);
						}

						if (!hasEnoughBalance) {
							if (phase === WEB3_MERGE_PHASE.PostMerge) {
								infoService.setSuccess(
									translationService.translate("web3_migration_success_post_alert")
								);
							} else {
								infoService.setInfo(
									translationService.translate("web3_migration_warning_nobalance_alert")
								);
							}

							return navigation.createUrlTree(basePath);
						}

						return true;
					}),
					catchError(() => {
						//error
						infoService.setError(translationService.translate("web3_migration_error_alert"));
						return of(navigation.createUrlTree(basePath));
					})
				);
			}

			return of(navigation.createUrlTree(basePath));
		})
	);
};
